import { posDb } from '../database/database';

// Operational modes a device can run in. Wire shape mirrors the server's
// ModeValues constants character-for-character — keep these strings in
// sync with Server/EasyposServer/Entities/System/Subscription.cs.
export type Mode =
  'all-in-one' | 'manager' | 'pos' | 'kitchen' | 'stock' | 'display';

export const ALL_MODES: readonly Mode[] = [
  'all-in-one',
  'manager',
  'pos',
  'kitchen',
  'stock',
  'display',
] as const;

export function isMode(s: unknown): s is Mode {
  return typeof s === 'string' && (ALL_MODES as readonly string[]).includes(s);
}

export type SubscriptionTier = 'free' | 'bronze' | 'silver' | 'gold';

export const ALL_TIERS: readonly SubscriptionTier[] = [
  'free',
  'bronze',
  'silver',
  'gold',
] as const;

// Display labels used in dev-tools picker, upgrade prompts, badges.
// Keep in sync with tier names; capitalise here so the wire shape stays
// lowercase strings everywhere else.
export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
};

export type SubscriptionStatus = 'trial' | 'active' | 'lapsed' | 'purged';

export interface IShopFeatures {
  allowedModes: Mode[];
  maxDevicesTotal: number;
  maxDevicesPerMode: { [mode: string]: number };
  allowsRealtime?: boolean;
  allowsMultiStaffPins?: boolean;
  allowsQrOrdering?: boolean;
  allowsAdminMonitoring?: boolean;
  allowsShopWalks?: boolean;
  allowsInventory?: boolean;
  // B1 — Cash management module. When false the PWA hides drawer/session
  // surfaces and the server treats cash sales as bookkeeping only.
  allowsCashManagement?: boolean;
}

export interface ISubscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  customerId?: string | null;
  validUntil?: string | null;
  trialStartedUtc?: string;
  lastActivityUtc?: string;
  features: IShopFeatures;
}

// Compact subscription snapshot attached to each ShopAccess entry on
// the LoginAccessToken. Matches the server's ShopAccessSubscription.
export interface IShopAccessSubscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  trialDaysRemaining: number;
  // Optional Inventory/Procurement module gate. When false (the default
  // on every current tier), the PWA hides stock / stocktake / suppliers /
  // POs / deliveries / reorder, and sales server-side skip the recipe
  // snapshot + decrement. See ShopFeatures.AllowsInventory.
  allowsInventory?: boolean;
  // B1 — Cash management module gate.
  allowsCashManagement?: boolean;
  // N.7.4 — per-staff PINs (Silver+). When false the shop cannot record who
  // took a sale, so the till hides the "who is serving" bar rather than
  // offering a keypad the server would refuse. See ShopFeatures.AllowsMultiStaffPins.
  allowsMultiStaffPins?: boolean;
}

export interface IDeviceRegistration {
  shopName: string;
  deviceId: string;
  displayName: string;
  allowedModes: Mode[];
  defaultMode: Mode;
  registeredByLoginId: string;
  registeredUtc: string;
  lastSeenUtc: string;
  lastUserLoginId?: string | null;
  status: 'active' | 'deregistered';
}

// Permissive helper mirroring server-side ShopCan.UseMode. Returns true
// when the requested mode is in the shop's features.allowedModes, false
// otherwise. Phase O flips this to enforce; today it is only used by
// the PWA's mode picker (when shipped) to grey-out unavailable options.
export function shopCan(
  features: IShopFeatures | null | undefined,
  mode: string
): boolean {
  if (!isMode(mode)) return false;
  // Defensive: a shop without a features payload defaults to free-tier
  // (all-in-one only). Aligns with the server's ShopCan.UseMode.
  const allowed = features?.allowedModes ?? ['all-in-one'];
  return allowed.includes(mode as Mode);
}

const DEVICE_ID_KEY = 'device-id';

// UUID v4 generator using the Web Crypto API. crypto.randomUUID is
// available in modern browsers; the fallback path uses getRandomValues +
// the standard v4 layout for older runtimes.
function newDeviceId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback: 16 random bytes, set the version (4) + variant bits, format
  // as a standard UUID string.
  const bytes = new Uint8Array(16);
  c?.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    ''
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Returns the stable device id for this physical tablet, generating + persisting
// a new UUID v4 on first run. Survives reloads, browser restarts, and PWA
// installs. NOT shared across browsers / private windows / different user
// profiles on the same hardware — those are different "devices" by design.
//
// The id is independent of the user logged in. Multiple users logging in
// and out on the same tablet share the same device id; that is what makes
// it useful as a "this physical tablet" identifier for the registration
// ceremony.
export async function getOrCreateDeviceId(): Promise<string> {
  const existing = await posDb.master.getRecord<string>(DEVICE_ID_KEY);
  if (existing?.value) return existing.value;
  const fresh = newDeviceId();
  await posDb.master.setRecord(DEVICE_ID_KEY, fresh);
  return fresh;
}

// Per (shopId, deviceId) currently-selected mode. Persisted in IndexedDB
// so a tablet remembers which mode it was using for each shop it touches.
// Default for a fresh (shop, device) pair is `all-in-one`; once the
// registration ceremony lands the default will read from the registration's
// defaultMode.
const MODE_KEY_PREFIX = 'device-mode/';

function modeKey(shopName: string, deviceId: string): string {
  return `${MODE_KEY_PREFIX}${shopName.toLowerCase()}/${deviceId.toLowerCase()}`;
}

export async function getDeviceMode(
  shopName: string,
  deviceId: string
): Promise<Mode> {
  const r = await posDb.master.getRecord<Mode>(modeKey(shopName, deviceId));
  const value = r?.value;
  return isMode(value) ? value : 'all-in-one';
}

export async function setDeviceMode(
  shopName: string,
  deviceId: string,
  mode: Mode
): Promise<void> {
  if (!isMode(mode)) throw new Error(`Unknown mode '${mode}'.`);
  await posDb.master.setRecord(modeKey(shopName, deviceId), mode);
}

// Outcome of the device-binding gate. Used by app-index after the user
// profile loads to decide whether to allow the user into the app, force
// them into a specific shop, or reject + log them out.
export type DeviceBindingOutcome =
  | { action: 'allow'; autoSelectShop?: string; forcedMode?: Mode }
  | { action: 'reject'; reason: string; boundShopName: string };

export interface IBindingResponse {
  kind: 'personal' | 'shop-owned';
  shopName?: string;
  defaultMode?: string;
  // Note: more fields may be present (allowedModes etc.); we only need
  // these three for the gate.
}

export interface IBindingEvaluatableProfile {
  shopAccess: { shopName: string; active: boolean }[];
}

// Pure function (no side effects) that decides what to do for a given
// (binding, profile) pair. Tested directly; called from app-index in the
// real flow.
//
//   - personal binding → allow with no override; the existing
//     restoreActiveShopFor flow takes over.
//   - shop-owned binding + user is a member of the bound shop →
//     allow with autoSelectShop set + forcedMode applied (if the
//     stored defaultMode is a known Mode).
//   - shop-owned binding + user is NOT a member → reject with a
//     reason string the rejection screen surfaces.
export function evaluateDeviceBinding(
  profile: IBindingEvaluatableProfile | null | undefined,
  binding: IBindingResponse | null | undefined
): DeviceBindingOutcome {
  if (!binding || binding.kind === 'personal') {
    return { action: 'allow' };
  }
  // shop-owned
  const boundShop = (binding.shopName ?? '').toLowerCase();
  if (!boundShop) {
    // Server returned shop-owned without a shop name — unexpected. Treat
    // as personal so we don't lock anyone out of an inconsistent state.
    return { action: 'allow' };
  }
  const isMember =
    profile?.shopAccess?.some(
      (s) => s.active && s.shopName.toLowerCase() === boundShop
    ) ?? false;
  if (!isMember) {
    return {
      action: 'reject',
      boundShopName: boundShop,
      reason: `This device is configured for ${boundShop}. Sign in with an account that has access to that shop.`,
    };
  }
  const forcedMode = isMode(binding.defaultMode)
    ? binding.defaultMode
    : undefined;
  return {
    action: 'allow',
    autoSelectShop: boundShop,
    forcedMode,
  };
}
